'use strict';

var _ = require('lodash');

function ViewManager (page) {
  this.page = page;
  this.active = '';
  this._views = {};
  this._viewIndices = {};
  this._count = 0;
  this._stack = [];
}

///////////////////////////////////////////////////////////////////////////////////////////////////
// Getters
///////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Return the currently active view
 *
 * @return {String} view id
 */
ViewManager.prototype.getActive = function () {
  return this.active;
};

/**
 * Returns the number of registered views
 *
 * @return {Number}
 */
ViewManager.prototype.getCount = function () {
  return this._count;
};

ViewManager.prototype.getViewByIndex = function (index) {
  var id;

  _.find(this._viewIndices, function (indexPos, viewId) {
    id = viewId;
    return indexPos === index;
  });

  return id;
};

/**
 * Returns the index of the view, i.e. indicates when the view was registered in relation to
 * the other views
 *
 * @param  {String} id view id
 */
ViewManager.prototype.getViewIndex = function (id) {
  return this._viewIndices[id] !== undefined ? this._viewIndices[id] : -1;
};

/*
 * Returns the default view for a page
 */
ViewManager.prototype.getDefault = function () {
  // This is done so if we need later to specify a `default=""` attribute on the
  // directive we can do it without breaking existing behavior
  return this.getViewByIndex(0);
};

/**
 * Return a boolean indicting whether or not the specified view is active
 *
 * @param  {String}  id view id
 * @return {Boolean} true if active
 */
ViewManager.prototype.isActive = function (id) {
  return id === this.active;
};

/**
 * Returns a boolean indicating whether or not the specified view is registered
 *
 * @param  {String}  id view id
 * @return {Boolean} true if registered
 */
ViewManager.prototype.viewExists = function (id) {
  return this._viewIndices[id] !== undefined;
};

///////////////////////////////////////////////////////////////////////////////////////////////////
// Setters
///////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Register a view
 *
 * @param  {String|Object} id view id or view object containing the id
 */
ViewManager.prototype.register = function (idOrView) {
  var id;
  var viewObj;

  if (typeof idOrView === 'string') {
    id = idOrView;
    viewObj = {id: id};
  } else {
    id = idOrView.id;
    viewObj = idOrView;
  }

  if (!this.viewExists(id)) {
    var index = this._count++;
    this._views[id] = viewObj;
    this._viewIndices[id] = index;
    return index;
  } else {
    throw new Error('View already registered: ' + id);
  }
};

/**
 * Set the specified view as "active"
 *
 * @param {String} id view id
 */
ViewManager.prototype.setActive = function (id) {
  var current = this.active;

  if (this.viewExists(id)) {
    var idx1 = current && this.getViewIndex(current) || 0;
    var idx2 = this.getViewIndex(id);

    // if the new view comes before then we are "returning" to it
    // so revert the stack to that view
    if (idx2 <= idx1 && !!~this._stack.indexOf(current)) {
      this.active = id;
      this.stackRevertTo(idx2);
    } else if (this.stackPeak() !== id) {
      this.active = id;
      this.stackPush(id);
    }
  } else {
    throw new Error('View does not exist: ' + id);
  }
};

/**
 * Remove the specified view
 *
 * @param  {String} id view id
 */
ViewManager.prototype.removeView = function (id) {
  if (id === this.active) {
    this.active = '';
  }

  delete this._views[id];
  delete this._viewIndices[id];
  this._count--; // decrement the count

  // reset the stack
  this.stackReset();
};

/**
 * Unregisters all views, effectively resetting the state.
 * This should be used when the parent controller's scope is being removed.
 */
ViewManager.prototype.removeAll = function() {
  this.page = null;
  this.active = '';
  this._views = {};
  this._viewIndices = {};
  this._count = 0;
  this._stack = [];
};

///////////////////////////////////////////////////////////////////////////////////////////////////
// Stack Methods
///////////////////////////////////////////////////////////////////////////////////////////////////

ViewManager.prototype.stackGet = function () {
  return this._stack;
};

ViewManager.prototype.stackPeak = function () {
  if (this._stack.length) {
    var index = this._stack.length - 1;
    return this._stack[index];
  }

  return null;
};

ViewManager.prototype.stackPop = function () {
  var len = this._stack.length;

  if (len) {
    var i = --len;
    return this._stack.splice(i,1)[0];
  }

  return null;
};

ViewManager.prototype.stackRevertTo = function (index) {
  var viewId = this._stack[index];

  // delete everything after index
  this._stack = this._stack.slice(0, index+1);
  return viewId;
};

ViewManager.prototype.stackPush = function (viewId) {
  if (this.viewExists(viewId)) {
    this._stack.push(viewId);
  } else {
    throw new Error('View does not exist: ' + viewId);
  }
};

ViewManager.prototype.stackReset = function () {
  this._active = '';
  this._stack = [];
};

module.exports = {
  _instances: {},
  create: function (pageId) {
    var viewManager = this._instances[pageId];

    if (!viewManager) {
      viewManager = new ViewManager(pageId);
      this._instances[pageId] = viewManager;
    }

    return viewManager;
  },
  destroy: function (pageId) {
    delete this._instances[pageId];
  },
  destroyAll: function () {
    _.forEach(this._instances, function (instance, pageId) {
      delete this._instances[pageId];
    }.bind(this));
  }
};


// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/layout/src/nav/view/ViewManager.js